// The dashboard × level picker itself — the one place the three states are drawn.
//
// Shared by the two screens that decide access: the grants dialog for someone who
// already exists, and the Create-user dialog, where the same choice is made before
// the account is born (Ohad 2026-07-26 — "אין לי שליטה על מה היא רואה" came from
// those being two separate steps). Both must offer exactly the same choice, so they
// render the same component rather than two lists that drift apart.
//
// Presentational on purpose: it holds no state and saves nothing. Who owns the draft
// and when it is written is the caller's decision, and it differs between the two.

export type Level = 'none' | 'view' | 'edit';

export interface Dashboard {
  id: string;
  name: string;
  route: string;
  /** A capability is a permission to DO something, not a place to go — it has no
      route, and no meaningful "edit". Drawn as yes/no (Ohad 2026-09-22). */
  capability?: boolean;
  purpose?: string;
}

/** Capabilities get two states, not three: you either hold it or you do not. */
const CAP_LEVELS: { value: Level; label: string; hint: string }[] = [
  { value: 'none', label: 'חסום', hint: 'לא מחזיק' },
  { value: 'view', label: 'מורשה', hint: 'מחזיק' },
];

interface Props {
  dashboards: Dashboard[];
  /** The draft on screen. A missing id means 'none'. */
  value: Record<string, Level>;
  /** What the server holds, when there is such a thing — rows that moved are marked. */
  baseline?: Record<string, Level>;
  /** Disabled account: may only have access taken away, never given. */
  disabled?: boolean;
  onPick: (dashboardId: string, level: Level) => void;
}

export const LEVELS: { value: Level; label: string; hint: string }[] = [
  { value: 'none', label: '—', hint: 'אין גישה' },
  { value: 'view', label: 'צפייה', hint: 'רואה, לא משנה' },
  { value: 'edit', label: 'עריכה', hint: 'רואה ומשנה' },
];

export const levelOf = (g: Record<string, Level>, id: string): Level => g[id] || 'none';

export default function GrantPicker({ dashboards, value, baseline, disabled = false, onPick }: Props) {
  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
      {dashboards.map((d) => {
        const current = levelOf(value, d.id);
        const moved = !!baseline && current !== levelOf(baseline, d.id);
        const levels = d.capability ? CAP_LEVELS : LEVELS;
        return (
          <div key={d.id} className={`flex items-center justify-between px-3 py-2.5 ${moved ? 'bg-blue-50/40' : ''}`}>
            <div>
              <div className="text-sm text-gray-800">
                {d.name}
                {moved && <span className="mr-2 text-[11px] text-blue-600">שונה</span>}
              </div>
              {/* A capability has no address; describe what it does instead. For a
                  board, dir=ltr — in an RTL box a path renders as "exec/ask/", the
                  leading slash jumps to the end and reads like a typo. */}
              {d.capability
                ? <div className="text-xs text-gray-500 max-w-md">{d.purpose}</div>
                : <div dir="ltr" className="text-xs text-gray-500 text-right">{d.route}</div>}
            </div>
            <div className="flex items-center gap-1">
              {levels.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  title={disabled && l.value !== 'none' ? 'החשבון מושבת' : l.hint}
                  disabled={disabled && l.value !== 'none' && current !== l.value}
                  onClick={() => onPick(d.id, l.value)}
                  className={`text-xs rounded-md px-3 py-1.5 border disabled:opacity-40 disabled:cursor-not-allowed ${
                    current === l.value
                      ? l.value === 'edit'
                        ? 'bg-blue-600 text-white border-blue-600 font-medium'
                        : l.value === 'view'
                          ? 'bg-gray-800 text-white border-gray-800 font-medium'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
